import React, { useEffect, useMemo, useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { combineStatuses, isLoading, isNormal, useData, useStatus } from 'resift';
import { useDispatch } from 'react-redux';
import importAndValidateFile from 'fetches/entitySourceFiles/importAndValidateFile';
import makeUpdateEntitySourceFile from 'fetches/entitySourceFiles/makeUpdateEntitySourceFile';
import makeInitializeEntityFileSync from 'fetches/entitySourceFiles/makeInitializeEntityFileSync';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _partition from 'lodash/partition';

import Dialog from '@material-ui/core/Dialog';
import Button from '@material-ui/core/Button';
import LoadingButton from 'atoms/LoadingButton';
import Tooltip from '@material-ui/core/Tooltip';
import CsvUploadLoading from 'molecules/csvUpload/CsvUploadLoading';
import CsvUploadIssues from 'molecules/csvUpload/CsvUploadIssues';
import CsvUploadSuccess from './PageCsvUploadSuccess';

import * as Types from 'pages/Pages/typings';

interface Props {
  open: boolean;
  status: number;
  validationResultsResponse: { validationResults: Types.PageCsvValidationResult[] };
  onBackToPagesTab: () => void;
  onNewUpload: () => void;
  entityTypeKey: string;
}

function CsvAnalysisDialog({
  open,
  status: fileValidationStatus,
  validationResultsResponse,
  onBackToPagesTab,
  onNewUpload,
  entityTypeKey,
}: Props) {
  const classes = useStyles();
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [isProceeding, setIsProceeding] = useState(false);
  const [waitForSyncReady, setWaitForSyncReady] = useState(false);
  const fileValidationResultsResponse = useData(importAndValidateFile);
  const dispatch = useDispatch();
  const validationResults = _get(validationResultsResponse, 'validationResults', []);
  const entitySourceFileId = _get(fileValidationResultsResponse, 'entitySourceFileId');
  const updateEntitySourceFile = useMemo(
    () => makeUpdateEntitySourceFile(entitySourceFileId || ''),
    [entitySourceFileId],
  );
  const fileActiveStateUpdateStatus = useStatus(updateEntitySourceFile) || 1;
  const status = combineStatuses(fileValidationStatus, fileActiveStateUpdateStatus);
  const initializeEntityFileSync = makeInitializeEntityFileSync(entityTypeKey);
  const fileValidatedWithNoIssues = isNormal(status) && _isEmpty(validationResults);
  const initializeEntityFileSyncStatus = useStatus(initializeEntityFileSync);

  useEffect(() => {
    if (!open) {
      setShowSuccessScreen(false);
      setIsProceeding(false);
      setWaitForSyncReady(false);
    }
  }, [open]);

  useEffect(() => {
    // (note - pearl) This effect is to make sure initialize-sync has a chance to update status
    // before navigating to pages tab and calling the status endpoint there
    if (!waitForSyncReady || !isNormal(initializeEntityFileSyncStatus)) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      onBackToPagesTab();
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [waitForSyncReady, initializeEntityFileSyncStatus, onBackToPagesTab]);

  if (isLoading(status) && !isProceeding) {
    return (
      <Dialog open={open}>
        <div className={classes.root}>
          <CsvUploadLoading />
        </div>
      </Dialog>
    );
  }

  const [errors, warnings] = _partition(validationResults, ['type', 'error']);

  const disabled = !_isEmpty(errors);
  const isSuccess = showSuccessScreen || fileValidatedWithNoIssues;

  const handleContinue = () => {
    if (fileValidatedWithNoIssues) {
      return;
    }

    setShowSuccessScreen(true);
  };

  const handleProceedAndImport = async () => {
    if (!entitySourceFileId) {
      onBackToPagesTab();
      return;
    }

    setIsProceeding(true);
    await dispatch(updateEntitySourceFile({ isActive: true, sourceFileId: entitySourceFileId }));
    await dispatch(initializeEntityFileSync(entitySourceFileId));
    setWaitForSyncReady(true);
  };

  return (
    <Dialog open={open}>
      <div className={classes.root}>
        {isSuccess ? (
          <CsvUploadSuccess confirmationMessage="All Set!" />
        ) : (
          <CsvUploadIssues errors={errors} warnings={warnings} />
        )}
        <div className={classes.buttonContainer}>
          <Button onClick={onNewUpload} color="secondary">
            + Upload New File
          </Button>
          <Tooltip
            arrow
            placement="top"
            title={
              !isSuccess && disabled
                ? 'You must fix all errors before continuing. Please upload a new file with the errors addressed.'
                : ''
            }
          >
            <div>
              <LoadingButton
                variant="contained"
                onClick={isSuccess ? handleProceedAndImport : handleContinue}
                color="secondary"
                disabled={(!isSuccess && disabled) || isProceeding}
                loading={isSuccess && isProceeding}
                label={isSuccess ? 'Proceed and Import' : 'Continue'}
                className={classes.importButton}
              />
            </div>
          </Tooltip>
        </div>
      </div>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: 600,
    height: 740,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    maxHeight: 24,
    marginTop: 'auto',
    position: 'sticky',
    bottom: 0,
  },
  importButton: {
    textTransform: 'none',
  },
}));

export default CsvAnalysisDialog;
